import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Link as ScrollLink, animateScroll } from "react-scroll";
import { IconType } from 'react-icons';
import { TbArrowUp, TbChevronRight, TbCopy, TbMail, TbMapPin, TbPhone } from "react-icons/tb";

import { toastOptions } from 'src/constants/toast';
import LangMenu from 'src/components/langMenu/LangMenu';
import { DEFAULT_EMAIL } from 'src/constants/vars';
import { copyToClipBoard } from 'src/utils/global';

const FooterTitle = ({ children, icon: Icon }: { children: React.ReactNode; icon?: IconType }) => (
    <h4 className="relative mb-8 flex items-center gap-2 pb-4 text-xl font-semibold leading-tight tracking-brand text-white after:absolute after:bottom-0 after:start-0 after:h-0.5 after:w-8 after:rounded-full after:bg-primary-400">
        {Icon && <Icon className="h-5 w-5 text-primary-300" />}
        {children}
    </h4>
);

const Footer = () => {
    const { t } = useTranslation("common");

    const links = [
        { name: t("footer.links.home"), to: "hero" },
        { name: t("footer.links.about"), to: "aboutUs" },
        { name: t("footer.links.services"), to: "services" },
        { name: t("footer.links.features"), to: "features" }
    ];

    const phoneCopyHandler = async (phoneNumber: string) => {
        const copied = await copyToClipBoard(phoneNumber);
        if (copied) {
            toast.success(t("footer.copied"), toastOptions);
        }
    };

    const contactItem =
        "group flex w-full items-center gap-3.5 text-start text-base font-light leading-normal tracking-brand text-primary-50 transition-colors hover:text-primary-300";
    const contactIcon =
        "grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/5 text-primary-300 ring-1 ring-white/10 transition-all duration-300 group-hover:bg-primary-400 group-hover:text-white group-hover:ring-primary-400";

    return (
        <footer className="relative overflow-hidden bg-primary-900 pt-20 text-white">
            {/* Decorative glows */}
            <div aria-hidden className="pointer-events-none absolute -top-32 start-1/3 h-72 w-72 rounded-full bg-primary-500/20 blur-3xl" />
            <div aria-hidden className="pointer-events-none absolute -bottom-40 -end-20 h-80 w-80 rounded-full bg-dark-primary-500/30 blur-3xl" />

            <div className="container relative">
                <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-12 lg:gap-8">

                    {/* LOCATION */}
                    <div className="md:col-span-2 lg:col-span-5">
                        <FooterTitle icon={TbMapPin}>{t("footer.location")}</FooterTitle>
                        <div className="h-64 overflow-hidden rounded-2xl ring-1 ring-white/10 sm:h-80 lg:h-60">
                            <iframe
                                src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d364.7191544609056!2d15.105863091781243!3d32.33779617010926!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMzLCsDIwJzE2LjMiTiAxNcKwMDYnMjAuNyJF!5e0!3m2!1sar!2sly!4v1766155411217!5m2!1sar!2sly"
                                className="h-full w-full border-0 grayscale-[35%] transition duration-500 hover:grayscale-0"
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title={t("footer.map_title")}
                            ></iframe>
                        </div>
                    </div>

                    {/* LINKS (SCROLL) */}
                    <div className="lg:col-span-3 lg:col-start-6 xl:col-start-7">
                        <FooterTitle>{t("footer.links.title")}</FooterTitle>
                        <ul className="space-y-4">
                            {links.map((item, index) => (
                                <li key={`links-key-${index}`}>
                                    <ScrollLink
                                        to={item.to}
                                        href={`#${item.to}`}
                                        smooth={true}
                                        duration={500}
                                        offset={-70}
                                        className="group inline-flex cursor-pointer items-center gap-2 text-base font-light tracking-brand text-primary-50 transition-colors hover:text-primary-300"
                                    >
                                        <TbChevronRight className="h-4 w-4 text-primary-400 transition-transform duration-300 rtl:rotate-180 ltr:group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
                                        {item.name}
                                    </ScrollLink>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* CONTACT INFO */}
                    <div className="lg:col-span-4 xl:col-span-3">
                        <FooterTitle>{t("footer.contact_info")}</FooterTitle>
                        <ul className="space-y-4">
                            {[
                                { label: t("footer.phone1"), value: "+218924560537" },
                                { label: t("footer.phone2"), value: "+218915160537" },
                            ].map((phone) => (
                                <li key={phone.value}>
                                    <button type="button" className={contactItem} onClick={() => phoneCopyHandler(phone.value)}>
                                        <span className={contactIcon}><TbPhone className="h-5 w-5" /></span>
                                        <span className="flex-1 whitespace-nowrap text-start">{phone.label}</span>
                                        <TbCopy className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-70" />
                                    </button>
                                </li>
                            ))}

                            <li>
                                <a className={contactItem} href={`mailto:${DEFAULT_EMAIL}`}>
                                    <span className={contactIcon}><TbMail className="h-5 w-5" /></span>
                                    <span className="break-all sm:break-normal">{DEFAULT_EMAIL}</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-14 flex flex-col-reverse items-center justify-between gap-5 border-t border-white/10 py-6 sm:flex-row">
                    <p className="text-center text-sm font-light capitalize tracking-[0.5px] text-primary-100/80 sm:text-[0.9375rem]">
                        {t("footer.copyright")}
                    </p>
                    <div className="flex items-center gap-3">
                        <LangMenu />
                        <button
                            type="button"
                            onClick={() => animateScroll.scrollToTop({ duration: 600, smooth: "easeInOutQuart" })}
                            aria-label="Back to top"
                            className="grid h-10 w-10 place-items-center rounded-full bg-primary-400 text-white shadow-glow transition hover:-translate-y-0.5 hover:bg-primary-500"
                        >
                            <TbArrowUp className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
